import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { ActivePlanState, LegacyPlan } from "../../assets/interfaces/interfaces";
import { doSignOutClear } from "../../components/auth/helpers";

export const deriveLegacyPlan = (data): LegacyPlan | null => {
  if (!data || typeof data !== "object") return null;
  if (data.legacyPlan !== undefined) return data.legacyPlan;
  const sub = data.subscription;
  const provider = data.subscription_provider ?? sub?.provider;
  if (
    !data.comped
    && provider === "trade_gpt"
    && (sub?.name === "Pro" || sub?.name === "Premium")
  ) {
    return {
      type: sub.name,
      price_monthly: Number(sub.price ?? 0) / 100,
      billing_period: sub.billing_period ?? "month",
      migrate_by: sub.expires_at,
    };
  }
  return null;
};

const initialState: ActivePlanState = {
  plan: {
    promptsLeft: 0,
    subscription_type: "Free",
    subscription: {},
  }, // Initialized as an empty array
  status: "idle",
  error: null,
  migrationFlowOpen: false,
};
// Define the async thunk
export const fetchActivePlan = createAsyncThunk(
  "plan/fetchActivePlan",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/v2/subscriptions/active`,
        { withCredentials: true },
      );
      return data && typeof data === "object"
        ? { ...data, legacyPlan: deriveLegacyPlan(data) }
        : data;
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          // Lets log the user out if token is expired
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const fetchActivePlanV2 = createAsyncThunk(
  "plan/fetchActivePlanV2",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/v2/subscriptions/active`,
        { withCredentials: true },
      );
      return data && typeof data === "object"
        ? { ...data, legacyPlan: deriveLegacyPlan(data) }
        : data;
    }
    catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error) ? error.response?.status : "Unexpected error occurred",
      );
    }
  },
);

export const applyBundleSelection = createAsyncThunk(
  "plan/applyBundleSelection",
  async (
    { bundleIds, billingPeriod }: { bundleIds: string[]; billingPeriod?: "month" | "year" },
    { getState, rejectWithValue },
  ) => {
    try {
      const { plan } = (getState() as { plan: ActivePlanState }).plan;
      const { data } = await axios.post(
        `${process.env.REACT_APP_SPL_URL}/subscriptions/bundles`,
        {
          bundles: bundleIds,
          billing_period: billingPeriod
            ?? plan?.billingPeriod
            ?? plan?.subscription?.billing_period
            ?? "month",
        },
        { withCredentials: true },
      );
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

// Create the slice
export const activePlanSlice = createSlice({
  name: "active/plan",
  initialState,
  reducers: {
    // standard reducers here if needed
    updatePromptsLeft: (state, action: PayloadAction<number>) => {
      state.plan.promptsLeft = action.payload;
    },
    resetPlanStatus: (state) => {
      state.status = "idle";
      state.plan = initialState.plan; // Reset to initial plan state
      state.error = null; // Clear any errors
    },
    setMigrationFlowOpen: (state, action: PayloadAction<boolean>) => {
      state.migrationFlowOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivePlan.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchActivePlan.fulfilled,
        (state, action: PayloadAction<ActivePlanState["plan"]>) => {
          state.status = "succeeded";
          state.plan = action.payload ? { ...state.plan, ...action.payload } : state.plan;
        },
      )
      .addCase(fetchActivePlan.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch active plan";
      })
      .addCase(fetchActivePlanV2.fulfilled, (state, action) => {
        const v2 = action.payload as Partial<ActivePlanState["plan"]> | undefined;
        if (!v2 || typeof v2 !== "object") return;
        const { pro, modelOptions, ownedBundles, dailyPrompts, monthlySpend, billingPeriod, comped, subscription_provider, non_renewing, legacyPlan, scheduledChanges } = v2;
        if (non_renewing !== undefined) state.plan.non_renewing = non_renewing;
        if (pro !== undefined) state.plan.pro = pro;
        if (modelOptions !== undefined) state.plan.modelOptions = modelOptions;
        if (ownedBundles !== undefined) state.plan.ownedBundles = ownedBundles;
        if (dailyPrompts !== undefined) state.plan.dailyPrompts = dailyPrompts;
        if (monthlySpend !== undefined) state.plan.monthlySpend = monthlySpend;
        if (billingPeriod !== undefined) state.plan.billingPeriod = billingPeriod;
        if (comped !== undefined) state.plan.comped = comped;
        if (subscription_provider !== undefined) state.plan.subscription_provider = subscription_provider;
        if (scheduledChanges !== undefined) state.plan.scheduledChanges = scheduledChanges;
        state.plan.legacyPlan = legacyPlan ?? null;
      })
      .addCase(applyBundleSelection.fulfilled, (state, action) => {
        const res = action.payload as Partial<ActivePlanState["plan"]> | undefined;
        if (!res || typeof res !== "object") return;
        const { ownedBundles, dailyPrompts, monthlySpend, billingPeriod, scheduledChanges } = res;
        if (ownedBundles) state.plan.ownedBundles = ownedBundles;
        if (dailyPrompts !== undefined) state.plan.dailyPrompts = dailyPrompts;
        if (monthlySpend !== undefined) state.plan.monthlySpend = monthlySpend;
        if (billingPeriod !== undefined) state.plan.billingPeriod = billingPeriod;
        if (scheduledChanges !== undefined) state.plan.scheduledChanges = scheduledChanges;
      });
  },
});

export const { updatePromptsLeft, resetPlanStatus, setMigrationFlowOpen } = activePlanSlice.actions;

// Export the reducer from the slice
export default activePlanSlice.reducer;
